import { FetchProductsResponse } from "./productsApi"
import type { AppLocalization } from "../../../localization/appLocalization"
import { WeekDays } from "../productGroup/productGroupTypes"
import { GroupItem, ProductItem, ProductTableRow, ProductTableRowType } from "./productsTypes"

/**
 * Stable MRT row id for product/group rows (keeps row identity across reorders).
 * Falls back to the row index when type/id are missing so MRT never receives
 * colliding keys (e.g. `undefined-undefined`), which would corrupt its keyed
 * internal state and trigger a re-render loop.
 */
export const getProductRowId = (row: ProductTableRow, index: number): string =>
    row?.id != null && row?.type != null ? `${row.type}-${row.id}` : `row-${index}`

/** Transform products data with sortOrder sorting - use for SortProductsTab */
export function transformProductsDataWithSortOrder(data: FetchProductsResponse): ProductTableRow[] {
    const sortedGroups = [...data.productGroups].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))

    return sortedGroups.flatMap((group) => {
        const groupItem: GroupItem = {
            type: ProductTableRowType.GROUP,
            id: group.id,
            name: group.name,
            groupAvailabilityDetails: group.availabilityDetails,
            groupSecuritySettings: group.securitySettings,
            productNumber: undefined,
            description: undefined,
            price: undefined,
            isActive: group.isActive,
            sortOrder: undefined,
            isAdminOnly: undefined,
            marketingDetails: undefined,
            productPhoto: undefined,
            userDetails: undefined,
            offerDetails: undefined,
            productDescription: undefined,
            availabilityDetails: undefined,
            deadline: undefined,
            priceExclVat: undefined,
            priceInclVat: undefined,
            stock: undefined,
            orderDetails: undefined,
        }

        const products = [...group.products]
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map<ProductItem>((product) => ({
                type: ProductTableRowType.PRODUCT,
                groupId: group.id,
                ...product,
            }))

        return [groupItem, ...products]
    })
}

/**
 * Optimistically reorder the flat product rows after a drag, so the move shows
 * instantly without waiting for a refetch. Pure function — safe to unit test.
 *
 * - Dragging a product moves that single row (within or between groups).
 * - Dragging a group header moves the whole group block (header + its products).
 * After moving, each product's `groupId` is re-derived from its nearest preceding
 * group header, keeping membership consistent for subsequent drags.
 *
 * Returns the input unchanged when the move is a no-op or can't be resolved.
 */
export function reorderProductRows(
    rows: ProductTableRow[],
    draggingItem: ProductTableRow,
    targetItem: ProductTableRow,
): ProductTableRow[] {
    const fromIndex = rows.findIndex((row) => row.type === draggingItem.type && row.id === draggingItem.id)
    const targetIndex = rows.findIndex((row) => row.type === targetItem.type && row.id === targetItem.id)

    if (fromIndex === -1 || targetIndex === -1 || fromIndex === targetIndex) {
        return rows
    }

    // A "block" is a single product row, or a group header together with its products.
    const blockLength = draggingItem.type === ProductTableRowType.GROUP ? getGroupBlockLength(rows, fromIndex) : 1
    const targetBlockLength = targetItem.type === ProductTableRowType.GROUP ? getGroupBlockLength(rows, targetIndex) : 1

    const isCrossGroupProductMove =
        draggingItem.type === ProductTableRowType.PRODUCT &&
        targetItem.type === ProductTableRowType.PRODUCT &&
        draggingItem.groupId !== targetItem.groupId

    // Index in the original array where the block should land (before removal):
    // - product dropped on a group header → first in that group (right after the header)
    // - product dropped into another group → take the target's slot (before it), matching the
    //   API position (= the target product's position within its group); otherwise a downward
    //   drop would land one slot too low since removing the row doesn't shift the other group
    // - dragging down (same group) → after the whole target block
    // - dragging up → before the target block
    let landingIndex: number
    if (draggingItem.type === ProductTableRowType.PRODUCT && targetItem.type === ProductTableRowType.GROUP) {
        landingIndex = targetIndex + 1
    } else if (isCrossGroupProductMove) {
        landingIndex = targetIndex
    } else if (fromIndex < targetIndex) {
        landingIndex = targetIndex + targetBlockLength
    } else {
        landingIndex = targetIndex
    }

    // Removing the dragged block shifts later indices left by its length.
    const insertIndex = fromIndex < landingIndex ? landingIndex - blockLength : landingIndex

    return reassignGroupIds(moveBlock(rows, fromIndex, blockLength, insertIndex))
}

/** Number of rows belonging to a group: the header plus the products that follow it. */
function getGroupBlockLength(rows: ProductTableRow[], headerIndex: number): number {
    let length = 1

    for (let i = headerIndex + 1; i < rows.length && rows[i].type === ProductTableRowType.PRODUCT; i++) {
        length++
    }

    return length
}

/** Remove a contiguous block of length `blockLength` and re-insert it at `insertIndex`. */
function moveBlock(
    rows: ProductTableRow[],
    fromIndex: number,
    blockLength: number,
    insertIndex: number,
): ProductTableRow[] {
    const result = [...rows]
    const block = result.splice(fromIndex, blockLength)

    result.splice(insertIndex, 0, ...block)

    return result
}

/** Re-derive each product's `groupId` from the nearest preceding group header. */
function reassignGroupIds(rows: ProductTableRow[]): ProductTableRow[] {
    let currentGroupId: number | undefined

    return rows.map((row) => {
        if (row.type === ProductTableRowType.GROUP) {
            currentGroupId = row.id

            return row
        }

        return row.groupId === currentGroupId ? row : { ...row, groupId: currentGroupId }
    })
}

/** Transform products data without sortOrder sorting - use for AllProductsTab */
export function transformProductsData(data: FetchProductsResponse): ProductTableRow[] {
    return data.productGroups.flatMap((group) => {
        const groupItem: GroupItem = {
            type: ProductTableRowType.GROUP,
            id: group.id,
            name: group.name,
            groupAvailabilityDetails: group.availabilityDetails,
            groupSecuritySettings: group.securitySettings,
            productNumber: undefined,
            description: undefined,
            price: undefined,
            isActive: group.isActive,
            sortOrder: undefined,
            isAdminOnly: undefined,
            marketingDetails: undefined,
            productPhoto: undefined,
            userDetails: undefined,
            offerDetails: undefined,
            productDescription: undefined,
            availabilityDetails: undefined,
            deadline: undefined,
            priceExclVat: undefined,
            priceInclVat: undefined,
            stock: undefined,
            orderDetails: undefined,
        }

        const products = group.products.map<ProductItem>((product) => ({
            type: ProductTableRowType.PRODUCT,
            groupId: group.id,
            ...product,
        }))

        return [groupItem, ...products]
    })
}

const WEEK_DAY_ORDER = Object.values(WeekDays)

export const isWeekDay = (value: string): value is WeekDays => WEEK_DAY_ORDER.some((day) => day === value)

export const formatWeekDays = (l10n: AppLocalization, weekDays: ReadonlyArray<string>): string =>
    weekDays.map((day) => l10n.getStringForEnum("product-group-form-week-day", day, day)).join(", ")

/** Chips are toggled in any order; the API gets the days Monday-first like the product group form shows them. */
export const sortWeekDays = (weekDays: ReadonlyArray<WeekDays>): WeekDays[] =>
    [...weekDays].sort((a, b) => WEEK_DAY_ORDER.indexOf(a) - WEEK_DAY_ORDER.indexOf(b))
